import { Box, Flex, Heading } from "@biblioteksentralen/react";
import { LocalizedField, isLocalizedField } from "@libry-content/localization";
import { ComponentPropsWithRef, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { CalendarDoorElement } from "./types";

const gridSize = 8;

const Iframe = (props: ComponentPropsWithRef<"iframe">) => (
  <Box css={{ "& iframe": { height: "38rem", "@media (max-width: 39rem)": { height: "34rem" } } }}>
    <iframe {...props} ref={props.ref} />
  </Box>
);

export const WordSearch: CalendarDoorElement<"adventCalendarDoorWordSearch"> = ({
  door,
  setSolvedTime,
  setAnswered,
}) => {
  const { t, ts, lang } = useTranslation();
  const words = Object.entries(door.words ?? {})
    ?.filter(([key, value]) => key.startsWith("word") && isLocalizedField(value))
    .map(([, value]) => ts(value as LocalizedField));

  const wordsUpperCase = words?.map((word) => word?.toString().toUpperCase());
  const [isReady, setIsReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isOnServer = typeof window === "undefined";
  const christmasWidgetsUrl = process.env.NEXT_PUBLIC_CHRISTMAS_WIDGETS_APP_URL;
  const [intervalId, setIntervalId] = useState<ReturnType<typeof setTimeout> | undefined>(undefined);

  const pollForReadyState = useCallback(() => {
    const newIntervalId = setInterval(() => {
      iframeRef.current?.contentWindow?.postMessage({ sender: "PARENT", message: "READY_CHECK" }, "*");
    }, 500);

    setIntervalId(newIntervalId);

    return () => clearInterval(newIntervalId);
  }, []);

  const sendPropsToIframe = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { sender: "PARENT", message: "SETUP", gridSize, words: wordsUpperCase },
      "*"
    );
  }, [wordsUpperCase]);

  useEffect(() => {
    if (isOnServer) return;

    const handleMessageFromIframe = ({ data }: any) => {
      if (data.message === "READY") {
        setIsReady(true);
        if (intervalId) clearInterval(intervalId);
        sendPropsToIframe();
      }

      if (data.message === "SOLVED") {
        if (isNaN(Number(data.time))) {
          console.error("Received solved time", data.time);
          setAnswered("correct");
        } else {
          setSolvedTime(Number(data.time));
          setAnswered("correct");
        }
      }
    };

    window.addEventListener("message", handleMessageFromIframe);

    return () => window.removeEventListener("message", handleMessageFromIframe);
  }, [isOnServer, intervalId, sendPropsToIframe, isReady, setSolvedTime, setAnswered]);

  if (!christmasWidgetsUrl) {
    console.error("Missing env variable NEXT_PUBLIC_CHRISTMAS_WIDGETS_APP_URL");
    return <></>;
  }

  return (
    <>
      {!isReady && (
        <Flex justifyContent="center" position="absolute">
          <Heading fontSize="xl" paddingTop="1rem">
            {t("Vennligst vent mens spillet laster inn..")}
          </Heading>
        </Flex>
      )}
      <Iframe
        src={`${christmasWidgetsUrl}/${lang}/wordsearch`}
        ref={iframeRef}
        width="100%"
        onLoad={pollForReadyState}
        sandbox="allow-scripts"
      />
    </>
  );
};
